import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Button from '../../components/ui/Button/Button';
import Card from '../../components/ui/Card/Card';
import { getVersionSummary } from '../../controllers/versions';
import './VersionReviewScreen.css';

const VersionReviewScreen = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { tableName, elementId, elementName, backRoute } = location.state || {};
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [versionSummary, setVersionSummary] = useState(null);

  useEffect(() => {
    if (tableName && elementId) {
      fetchVersionSummary();
    }
  }, [tableName, elementId]);


  const fetchVersionSummary = async () => {
    if (!tableName || !elementId) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await getVersionSummary({
        table_name: tableName,
        element_id: elementId
      });
      
      if (response.data && response.data.success) {
        setVersionSummary(response.data.version_summary_json);
      } else {
        setError('Failed to fetch version summary');
      }
    } catch (err) {
      console.error('Error fetching version summary:', err);
      setError(err.message || 'Failed to fetch version summary');
    } finally {
      setLoading(false);
    }
  };


  const parseVersionSummary = (summaryArray) => {
    if (!Array.isArray(summaryArray)) return [];
    
    return summaryArray
      .map((item) => ({
        date: item.date_string,
        summary: item.change_summary,
        boldString: item.bold_string,
        timestamp: new Date(item.date_string).getTime()
      }))
      .sort((a, b) => b.timestamp - a.timestamp); // Sort by newest first
  };

  const highlightBoldText = (summary, boldString) => {
    if (!boldString || !summary) return summary;
    
    // Split the bold string into individual words
    const boldWords = boldString.split(/\s+/).filter(word => word.length > 0);
    
    if (boldWords.length === 0) return summary;
    
    // Create a regex pattern that matches any of the bold words (case-insensitive)
    const escapedWords = boldWords.map(word => word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
    const regex = new RegExp(`\\b(${escapedWords.join('|')})\\b`, 'gi');
    
    // Split the summary by the regex and highlight matching words
    const parts = summary.split(regex);
    
    return parts.map((part, index) => {
      // Check if this part matches any of the bold words (case-insensitive)
      const isBoldWord = boldWords.some(word => 
        part.toLowerCase() === word.toLowerCase()
      );
      
      if (isBoldWord) {
        // Handle special case for Pet_age -> Pet Age
        let displayText = part;
        if (part.toLowerCase() === 'pet_age') {
          displayText = 'Pet Age';
        } else {
          // For other words, replace underscores with spaces and capitalize properly
          displayText = part.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
        }
        return <strong key={index}>{displayText}</strong>;
      }
      return part;
    });
  };

  const renderTimeline = () => {
    if (!versionSummary) {
      return (
        <Card className="timeline-card">
          <div className="timeline-empty">
            <h3>No Version Timeline</h3>
            <p>No version summary data available for this element.</p>
          </div>
        </Card>
      );
    }
    
    const timelineData = parseVersionSummary(versionSummary);
    
    if (timelineData.length === 0) {
      return (
        <Card className="timeline-card">
          <div className="timeline-empty">
            <h3>No Version Timeline</h3>
            <p>No version changes found for this element.</p>
          </div>
        </Card>
      );
    }

    return (
      <Card className="timeline-card">
        <div className="timeline-header">
          <h3>Timeline Of Changes</h3>
        </div>
        <div className="timeline-container">
          <table className="version-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Change</th>
              </tr>
            </thead>
            <tbody>
              {timelineData.map((item, index) => (
                <tr key={index}>
                  <td className="version-date">{item.date}</td>
                  <td className="version-change">
                    {highlightBoldText(item.summary, item.boldString)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    );
  };


  const handleBackClick = () => {
    if (backRoute) {
      navigate(backRoute, { state: location.state });
    } else {
      navigate(-1); // Go back to previous page
    }
  };

  const getScreenTitle = () => {
    if (elementName) {
      return `${elementName} - Version Timeline`;
    }
    return `Version Timeline`;
  };

  return (
    <div className="version-review-screen">

      {error && (
        <Card className="error-card">
          <div className="error-message">
            <h3>Error</h3>
            <p>{error}</p>
            <Button onClick={fetchVersionSummary} variant="primary">
              Try Again
            </Button>
          </div>
        </Card>
      )}

      {loading && !error && (
        <Card className="loading-card">
          <div className="loading-message">
            <p>Loading version timeline...</p>
          </div>
        </Card>
      )}

      {!loading && !error && (
        <div className="versions-container">
          {renderTimeline()}
        </div>
      )}
    </div>
  );
};

export default VersionReviewScreen;
